import React, { useEffect, useState } from "react"
import { MapContainer, useMap, TileLayer } from "react-leaflet"
import MarkerClusterGroup from "react-leaflet-markercluster"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import { FullscreenControl } from "react-leaflet-fullscreen"
import "react-leaflet-fullscreen/styles.css"
import SensorMarker from "./marker"
import ConnectionLine from "./connection"
import MapInfoLabels from "./info.labels"
import TooltipControl from "./tooltip-control"
import { MapObject, MapViewProps, MapConnection } from "api/mapview/mapview.pb"

// Исправление отображения иконок для маркеров (из-за отсутствия стандартных изображений)
import markerIcon from "leaflet/dist/images/marker-icon.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"

export type { MapObject, MapConnection }

// Настройка иконки для маркеров
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})
L.Marker.prototype.options.icon = DefaultIcon

const AutoFitBounds: React.FC<{ objects: MapObject[] }> = ({ objects }) => {
    const map = useMap()
  
    useEffect(() => {
      if (objects.length > 0) {
        const validObjects = objects.filter(obj => obj.coords)
        
        if (validObjects.length > 0) {
          const bounds = L.latLngBounds(
            validObjects.map(obj => [obj.coords!.x, obj.coords!.y] as L.LatLngTuple)
          )
          map.fitBounds(bounds, { padding: [50, 50] })
        }
      } else {
        // Если нет объектов, показываем весь земной шар
        map.setView([0, 0], 2)
      }
    }, [map, objects])
  
    return null
}

function MapView(props: React.PropsWithChildren<MapViewProps>): JSX.Element  {
    const { center, zoom, objects, connections = [], ...rest } = props
    const [showTooltips, setShowTooltips] = useState(false)

    // Преобразуем центр из protobuf формата в Leaflet формат
    const leafletCenter: L.LatLngExpression = center 
        ? [center.x, center.y] 
        : [0, 0]

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <MapContainer center={leafletCenter} zoom={zoom} style={{ height: '100%', width: '100%' }} {...rest} >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a>"
                />
                <FullscreenControl />
                <TooltipControl onToggle={setShowTooltips} />
                
                {/* Отображение связей (под маркерами) */}
                {connections.map(connection => (
                    <ConnectionLine 
                        key={connection.id} 
                        connection={connection} 
                        objects={objects} 
                    />
                ))}
                
                <MarkerClusterGroup key={`cluster-${showTooltips}`}>
                    {objects.map(obj => (
                        <SensorMarker 
                            key={`${obj.id}-${showTooltips}`} 
                            object={obj} 
                            disableHoverTooltip={showTooltips}
                        />
                    ))}
                </MarkerClusterGroup>
                
                <AutoFitBounds objects={objects} />
                
                {/* Новый компонент для отображения информационных лейблов */}
                <MapInfoLabels objects={objects} visible={showTooltips} />
            </MapContainer>
        </div>
    )
}

MapView.defaultProps = {
    center: { x: 0, y: 0 },
    zoom: 2,
}

export default MapView